import { useState, useMemo } from 'react';
import {
  AddIcon,
  Alert,
  Button,
  PageHeader,
  Spinner,
  Switch,
} from '@billda/design-system';
import { useNavigate } from 'react-router-dom';
import { Permissions, usePermission } from '@billda/permissions-ui';
import { useSitesFilters } from '../hooks/useSitesFilters';
import { useSites } from '../hooks/useSites';
import { PageHeaderAction } from '../../../shared/components/layout/PageHeaderAction';
import { PageToolbar } from '../../../shared/components/layout/PageToolbar';
import { ChipTabs } from '../../../shared/components/ui/ChipTabs';
import { SitesListView } from '../components/v2/organisms/SitesListView';
import { EmptyStateSites } from '../components/v2/organisms/EmptyStateSites';
import { Routes } from '../../../routes';

type SiteStatusFilter = 'all' | 'active' | 'not-started';

const SITE_STATUS_TABS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Not Started', value: 'not-started' },
] as const;

const SitesPage = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<SiteStatusFilter>('all');

  const hasEditPermission = usePermission(Permissions.Sites.Edit);

  // Filter state management (includes debouncing)
  const { filters, searchValue, updateSearch, updateIncludeArchived } =
    useSitesFilters();

  // Data fetching with filters
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useSites(filters);

  const handleSiteClick = (siteId: number) => {
    navigate(`/sites/${siteId}`);
  };

  // Flatten all pages of sites
  const allSites = data?.pages.flatMap(page => page.items) ?? [];
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  // Client-side status filtering, with archived sites sunk to the bottom
  const sites = useMemo(() => {
    const statusFiltered =
      statusFilter === 'all'
        ? allSites
        : allSites.filter(site => {
            const progress = site.financialMetrics?.progress ?? 0;
            return statusFilter === 'active' ? progress > 0 : progress === 0;
          });
    // Stable sort keeps API order within the active and archived groups
    return [...statusFiltered].sort(
      (a, b) => Number(a.archivedAt != null) - Number(b.archivedAt != null)
    );
  }, [allSites, statusFilter]);

  const pageHeaderActions = (
    <PageHeaderAction
      label="New Site"
      onClick={() => navigate(Routes.sites.import)}
      icon={<AddIcon />}
      data-testid="new-site-button"
    />
  );

  const statusTabs = (
    <ChipTabs
      tabs={[...SITE_STATUS_TABS]}
      value={statusFilter}
      onChange={v => setStatusFilter(v as SiteStatusFilter)}
      data-testid="sites-status-tabs"
    />
  );

  const showArchivedToggle = hasEditPermission ? (
    <label className="mr-2 flex cursor-pointer items-center gap-2 type-body-sm text-content-secondary">
      <Switch
        checked={filters.includeArchived}
        onCheckedChange={updateIncludeArchived}
        data-testid="show-archived-toggle"
      />
      Show archived
    </label>
  ) : undefined;

  // Loading state (initial load only - not during refetch)
  if (isLoading && !data) {
    return (
      <div className="flex min-h-50 items-center justify-center p-6">
        <Spinner />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" className="mb-4">
        {error.message}
      </Alert>
    );
  }

  // Empty state
  if (allSites.length === 0 && !isLoading) {
    return (
      <div>
        <PageHeader title="Sites" actions={pageHeaderActions} />
        <PageToolbar
          tabs={statusTabs}
          searchValue={searchValue}
          onSearchChange={updateSearch}
          actions={showArchivedToggle}
        />
        <EmptyStateSites />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="min-h-0 flex-1 overflow-auto">
        <div>
          <PageHeader title="Sites" actions={pageHeaderActions} />

          <PageToolbar
            tabs={statusTabs}
            searchValue={searchValue}
            onSearchChange={updateSearch}
            actions={showArchivedToggle}
          />

          <SitesListView
            sites={sites}
            loading={isLoading}
            onItemClick={handleSiteClick}
          />

          {hasNextPage && (
            <div className="mt-8 mb-4 flex justify-center">
              <Button
                variant="outline"
                size="lg"
                loading={isFetchingNextPage}
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage
                  ? 'Loading...'
                  : `Load More (${sites.length} of ${totalCount})`}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SitesPage;
